"use client";

import { useState } from "react";
import { Button } from "@/components/input/Button";
import ErrorBox from "@/components/input/ErrorBox";
import {
  handleAutoSignIn,
  handleConfirmSignUp,
  handleResendConfirmationCode,
} from "@/services/Cognito/handlers";
import { useForm, type SubmitHandler } from "react-hook-form";

type FormInputs = {
  confirmationCode: number;
};

type SubmitStatus = "idle" | "submitting" | "success" | "error";

const VerifyEmailForm = ({
  email,
  onSuccess,
}: {
  email: string;
  onSuccess?: () => void;
}) => {
  const [error, setError] = useState<Error | undefined>();
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>("idle");
  const { register, handleSubmit } = useForm<FormInputs>();

  const onSubmit: SubmitHandler<FormInputs> = async (data) => {
    setSubmitStatus("submitting");
    setError(undefined);
    // if (!email || !password) return router.replace("/auth/signIn");
    try {
      const res = await handleConfirmSignUp({
        username: email,
        confirmationCode: data.confirmationCode.toString(),
      });
      switch (res.nextStep.signUpStep) {
        case "CONFIRM_SIGN_UP":
          setSubmitStatus("error");
          throw new Error("Failed to verify email. Please try again");
        case "DONE":
        case "COMPLETE_AUTO_SIGN_IN":
          setSubmitStatus("success");
          await handleAutoSignIn();
          if (typeof onSuccess === "function") onSuccess();
          break;
      }
    } catch (error: unknown) {
      setSubmitStatus("error");
      if (!(error instanceof Error)) {
        setError(new Error("Failed to verify email. Please try again"));
        return;
      }
      // Handle "already confirmed" variations from Cognito
      if (
        error.message.includes("User is already confirmed") ||
        error.message.includes("Current status is CONFIRMED")
      ) {
        // User is already verified - sign them in
        setSubmitStatus("success");
        await handleAutoSignIn();
        if (typeof onSuccess === "function") onSuccess();
        return;
      }
      // Improve common error messages
      let friendlyMessage = error.message;
      if (error.message.includes("Invalid verification code")) {
        friendlyMessage = "Invalid verification code. Please check the code and try again.";
      } else if (error.message.includes("expired")) {
        friendlyMessage = "This verification code has expired. Please request a new code.";
      }
      setError(new Error(friendlyMessage));
    }
  };

  function handleResendCode() {
    handleResendConfirmationCode(email).catch(console.error);
  }

  return (
    <div className="flex w-full flex-col rounded-xl border border-gray-300 bg-white px-8 pb-4 pt-8 sm:w-[32rem]">
      <h3 className="mb-4 text-2xl font-semibold">Check your email</h3>
      <div className="mb-2">
        <p className="mb-1">
          Your code is on the way. To log in, enter the code we sent to:
        </p>
        <p className="mb-1 break-all font-semibold">{email}</p>
        <p className="text-sm text-gray-600">It may take a minute to arrive.</p>
      </div>
      {/* Weird logic cause this https://github.com/orgs/react-hook-form/discussions/8622#discussioncomment-5774089  */}
      <form
        onSubmit={(event) => {
          void handleSubmit(onSubmit)(event);
        }}
        className="my-4 flex w-full flex-col space-y-3"
      >
        <label className="grid">
          Confirmation Code
          <span className="text-sm font-light text-gray-500">
            Can&apos;t find the email? Check your spam folder
          </span>
        </label>

        <input
          type="text"
          className="rounded border border-gray-900 p-2 text-center"
          disabled={submitStatus === "submitting"}
          {...register("confirmationCode", { required: true })}
        />
        {error && (
          <ErrorBox message={error.message} hide={() => setError(undefined)} />
        )}
        {submitStatus === "success" && (
          <div className="rounded-lg bg-green-50 p-3 text-center text-green-700">
            Email verified successfully! Signing you in...
          </div>
        )}

        <Button
          type="submit"
          className="rounded-lg text-base"
          disabled={submitStatus === "submitting"}
        >
          {submitStatus === "submitting" ? "Verifying..." : "Confirm"}
        </Button>
        <button
          onClick={handleResendCode}
          type="button"
          disabled={submitStatus === "submitting"}
          className="rounded-lg border border-black bg-transparent py-1.5 text-black hover:border-purple-400 hover:bg-purple-100 disabled:opacity-50"
        >
          Resend Code
        </button>
      </form>
    </div>
  );
};
export default VerifyEmailForm;
